import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, LifeBuoy, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { fetchSupportThread, sendSupportMessage, type SupportMessage } from '@/data/support';
import { apiError } from '@/lib/api-error';

const POLL_MS = 6000;

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const EVENT_TITLE: Record<string, string> = {
  invoice_issued: 'Invoice issued',
  invoice_paid: 'Invoice paid',
  limit_changed: 'Server limit changed',
  invoicing_approved: 'Invoice billing approved',
  advisor_reported: 'Advisor reported',
};

export function SupportEvent({ message }: { message: SupportMessage }) {
  return (
    <div className="flex justify-center" data-testid="support-event" data-event={message.event}>
      <div className="w-full max-w-sm rounded-xl border bg-muted/40 px-4 py-3 text-sm">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Bell className="h-3.5 w-3.5" />
          {EVENT_TITLE[message.event] ?? 'Update'}
        </div>
        <p className="mt-1 leading-snug">{message.body}</p>
        <p className="mt-1 text-[10px] text-muted-foreground">{when(message.created_at)}</p>
      </div>
    </div>
  );
}

export function SupportBubble({ message, mine }: { message: SupportMessage; mine: boolean }) {
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid="support-message" data-side={message.side}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <p className={`mt-1 text-[10px] ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
          {message.sender_name || message.sender_email} · {when(message.created_at)}
        </p>
      </div>
    </div>
  );
}

export default function SupportThread({ orgId, initialDraft = '' }: { orgId?: string; initialDraft?: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState(initialDraft);
  const endRef = React.useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['support-thread', orgId ?? 'active'],
    queryFn: () => fetchSupportThread(orgId),
    refetchInterval: POLL_MS,
  });

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [thread.data?.messages.length]);

  const send = useMutation({
    mutationFn: () => sendSupportMessage(draft.trim(), orgId),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['support-thread', orgId ?? 'active'] });
      queryClient.invalidateQueries({ queryKey: ['support-unread'] });
    },
    onError: (error) => toast.error(apiError(error, 'Could not send that.')),
  });

  const messages = thread.data?.messages ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="support-thread">
      <div className="border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground" data-testid="support-thread-intro">
        <LifeBuoy className="mr-1 inline h-3.5 w-3.5" />
        A conversation with BitSwan support. Everyone in the organisation can read it; BitSwan answers here and by email.
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="support-messages">
        {thread.isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="support-no-messages">
            Nothing yet. Write to BitSwan below.
          </p>
        ) : (
          messages.map((message) => message.kind === 'event'
            ? <SupportEvent key={message.id} message={message} />
            : <SupportBubble key={message.id} message={message} mine={message.side === 'org'} />)
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t p-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Write to BitSwan support…"
          data-testid="support-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
              e.preventDefault();
              send.mutate();
            }
          }}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Enter sends, Shift+Enter starts a new line.</span>
          <Button size="sm" onClick={() => send.mutate()} disabled={send.isPending || !draft.trim()} data-testid="support-send">
            {send.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
